import { Add } from '@tunasong/icons'
import type { Entity, Persisted } from '@tunasong/schemas'
import { Button, DndList, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { sortSpaces } from './space-sort.js'
import styles from './spaces-section.module.css'
import { SpacesSortMenu } from './spaces-sort-menu.js'
import { useSpacesMeta } from './use-spaces-meta.hook.js'
import { useSpacesSort } from './use-spaces-sort.hook.js'
import { useStableSpaceOrder } from './use-stable-space-order.hook.js'

/** Spaces shown before the "All N spaces" toggle */
const spacesCap = 6

export interface SpacesSectionProps {
  /** Top-level spaces, owned and shared, in any order */
  spaces: Persisted<Entity>[]
  loading: boolean
  /** A create is in flight */
  busy: boolean
  onNewSpace(): void
}

/**
 * Home's index of spaces: a compact list, quieter than the Continue cards, sorted by last activity (or by name,
 * the viewer's choice, remembered per browser), each row with its counts and members, and capped at six. Under
 * last activity the rows wait up to 800 ms for the counts, then never move during the visit. There is no spaces route, so "All N spaces" expands the list in place. Rows keep the entity menu and
 * accept items dragged onto them (the DndList row behaviour), which is how Unfiled items get filed.
 */
export const SpacesSection: FC<SpacesSectionProps> = props => {
  const { spaces, loading, busy, onNewSpace } = props
  const [expanded, setExpanded] = useState(false)
  const [sortOrder, setSortOrder] = useSpacesSort()

  const live = spaces.filter(space => !space.trash)
  const { getItemProps, activityOf, settled } = useSpacesMeta(live)
  const rows = useStableSpaceOrder({
    sorted: sortSpaces(live, sortOrder, activityOf),
    order: sortOrder,
    waitForMeta: sortOrder === 'activity',
    listLoaded: !loading,
    metaSettled: settled,
  })
  const shown = rows ?? []
  const visible = expanded ? shown : shown.slice(0, spacesCap)
  const hasMore = shown.length > spacesCap

  return (
    <section className={styles.section} aria-labelledby="home-spaces">
      <div className={styles.headingRow}>
        <Typography id="home-spaces" variant="h2" className={styles.heading}>
          Spaces{live.length > 0 ? <span className={styles.count}> {live.length}</span> : null}
        </Typography>
        <div className={styles.actions}>
          {live.length > 0 ? <SpacesSortMenu value={sortOrder} onChange={setSortOrder} /> : null}
          <Button
            className={styles.ghost}
            disabled={busy}
            onClick={onNewSpace}
            startIcon={<Add />}
            data-testid="create-new-space"
          >
            New space
          </Button>
        </div>
      </div>
      <DndList
        id="home-spaces-list"
        entities={visible}
        isLoading={loading || rows === undefined}
        getItemProps={getItemProps}
        itemLayout="columns"
        showItemType={false}
        optionsKey="home-spaces"
        placeholder="No spaces yet"
      />
      {hasMore ? (
        <Button
          className={`${styles.ghost} ${styles.toggle}`}
          aria-expanded={expanded}
          aria-controls="home-spaces-list"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show fewer' : `All ${shown.length} spaces`}
        </Button>
      ) : null}
    </section>
  )
}

export default SpacesSection
